import {MeshCellValueFormatKind} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormatKind';
import {IMeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ADate} from '@mintdata/mint-kit/api/values/ADate';
import {ADateTime} from '@mintdata/mint-kit/api/values/ADateTime';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {ATime} from '@mintdata/mint-kit/api/values/ATime';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {format} from 'ssf';

export interface IMeshCellValueFormat
{
    readonly kind:MeshCellValueFormatKind,
    readonly formatString:string;
    readonly id:string;

    formatCode():string;

    formatValue(value:IValue):string;

    getTextColor(value:IValue, cellSettings:IMeshCellSettings):string;

    digitsAfterPoint():number;

    isStringOfThisFormat(formattedValue:string):boolean;

    extractValue(formattedValue:string):IValue;
}

export class ValueFormat implements IMeshCellValueFormat
{
    readonly formatString:string;
    readonly kind:MeshCellValueFormatKind;
    readonly id:string;
    readonly formatChecker:(value:string) => boolean;
    readonly valueExtractor:(formattedString:string) => IValue;

    public constructor(id:string, kind:MeshCellValueFormatKind, formatString:string,
                       formatChecker:(value:string) => boolean = (val) =>
                       {
                           return false;
                       },
                       valueExtractor:(formattedString:string) => IValue = value =>
                       {
                           const numberPart = RegExp(/^-?[\d,]*\.?\d*$/)
                               .exec(value.trim());
                           if( !numberPart )
                           {
                               if( value.toLowerCase() === 'true' || value.toLowerCase() === 'false' )
                               {
                                   return ABoolean.fromString(value);
                               }
                               return new AString(value);
                           }
                           let withoutDelimiters = numberPart[0].replace(/,/gm, '');
                           if( withoutDelimiters.startsWith('.') )
                           {
                               withoutDelimiters = '0' + withoutDelimiters;
                           }
                           return new ANumber(Number.parseFloat(withoutDelimiters));
                       })
    {
        this.kind = kind;
        this.formatString = formatString;
        this.id = id;
        this.formatChecker = formatChecker;
        this.valueExtractor = valueExtractor;
    }

    formatCode():string
    {
        switch( this.kind )
        {
            case MeshCellValueFormatKind.General:
            case MeshCellValueFormatKind.PlainText:
            case MeshCellValueFormatKind.MaskedText:
                return 'G';
            case MeshCellValueFormatKind.Number:
                return 'F' + this.digitsAfterPoint();
            case MeshCellValueFormatKind.Percent:
                return 'P' + this.digitsAfterPoint();
            case MeshCellValueFormatKind.Accounting:
            case MeshCellValueFormatKind.Financial:
            case MeshCellValueFormatKind.Currency:
            case MeshCellValueFormatKind.CurrencyRounded:
                if( this.formatString.startsWith('#') )
                {
                    return this._getFormatCodePrefix() + 'C' + this.digitsAfterPoint();
                }
                else
                {
                    return 'C' + this.digitsAfterPoint() + this._getFormatCodeSuffix();
                }
            case MeshCellValueFormatKind.Date:
                if( this.formatString === 'd-mmm-yy' || this.formatString === 'dd-mmm-yy' )
                {
                    return 'D1';
                }
                if( this.formatString === 'd-mmm' || this.formatString === 'dd-mmm' )
                {
                    return 'D2';
                }
                if( this.formatString === 'mmm-yy' )
                {
                    return 'D3';
                }
                if( this.formatString === 'm/d/yy' || this.formatString === 'mm/dd/yy' )
                {
                    return 'D4';
                }
                if( this.formatString === 'mm/dd' )
                {
                    return 'D5';
                }
                return 'D';
            case MeshCellValueFormatKind.DateTime:
            case MeshCellValueFormatKind.Time:
                if( this.formatString === 'm/d/yy h:mm' )
                {
                    return 'D4';
                }
                if( this.formatString === 'h:mm:ss AM/PM' )
                {
                    return 'D6';
                }
                if( this.formatString === 'h:mm AM/PM' )
                {
                    return 'D7';
                }
                if( this.formatString === 'h:mm:ss' )
                {
                    return 'D8';
                }
                return 'D';
            case MeshCellValueFormatKind.Duration:
                if( this.formatString === 'h:mm:ss' )
                {
                    return 'D8';
                }
                return 'D';
            case MeshCellValueFormatKind.Fraction:
                return 'G';
            case MeshCellValueFormatKind.Image:
                return 'G';
            default:
                return 'G';

        }
    }

    formatValue(value:IValue):string
    {
        if( Type.isInstanceOf(value, Type.Date) )
        { //should be handled specially.
            return format(this.formatString, (value as ADate).toExcelDate());
        }
        else if( Type.isInstanceOf(value, Type.DateTime) )
        {
            return format(this.formatString, (value as ADateTime).toExcelDate());
        }
        else if( Type.isInstanceOf(value, Type.Time) )
        {
            return format(this.formatString, (value as ATime).toExcelTime());
        }
        else if(
            Type.isInstanceOf(value, Type.String) &&
            this.kind !== MeshCellValueFormatKind.General &&
            this.kind !== MeshCellValueFormatKind.PlainText
        )
        {
            const numberValue:IValue = CoercionUtils.coerce(value, Type.Number, null);

            if( !numberValue.isError )
                value = numberValue;
        }

        if( Type.isInstanceOf(value, Type.Byte) ||
            Type.isInstanceOf(value, Type.Integer) ||
            Type.isInstanceOf(value, Type.Number) ) //normally formats are applicable only for numeric types.
        {
            const val = CoercionUtils.coerce(value, Type.Number, null);
            if( val.isError || val.isEmpty )
            {
                return value.toString();
            }
            const numberValue = (val as ANumber).value;
            if( this.kind === MeshCellValueFormatKind.Timestamp )
            {
                return format(this.formatString, ADateTime.fromJSDate(new Date(numberValue * 1000)).toExcelDate());
            }
            if( this.kind === MeshCellValueFormatKind.Date || this.kind === MeshCellValueFormatKind.DateTime
                || this.kind === MeshCellValueFormatKind.Time )
            {
                //SSF behave like excel - it cannot format negative values as dates, times or datetimes. So we simply
                //convert incoming value to string. Should be rewritten to support dates earlier than 1.01.1900 0:00
                if( numberValue.valueOf() < 0 )
                {
                    return value.toString();
                }
            }
            return format(this.formatString, numberValue).trim();
        }
        else if( Type.isInstanceOf(value, Type.Frame) )
        {
            const frame:IFrame = value as IFrame;

            return frame.isEmpty ? '' : CoercionUtils.coerce(frame, Type.String, AString).toString();
        }
        else
        {
            return value.toString(); //all other cases
        }
    }

    getTextColor(value:IValue, cellSettings:IMeshCellSettings):string
    {
        let textColor = '#000000';
        if( cellSettings && cellSettings.textColor )
        {
            textColor = cellSettings.textColor;
        }
        const val = CoercionUtils.coerce(value, Type.Number, null);
        if( val.isError || val.isEmpty )
        {
            return textColor;
        }
        const numValue = (val as ANumber).value;
        //Index to choose part to select color value from. 0 - for positives, 1 - for negatives, 2 - for zeros
        let partIndex;
        if( numValue == 0 )
        {
            partIndex = 2;
        }
        else if( numValue > 0 )
        {
            partIndex = 0;
        }
        else
        { //negatives
            partIndex = 1;
        }
        if( this.formatString.indexOf(';') > -1 )
        { //format actually has separate parts for negatives and positives
            const parts = this.formatString.split(';');
            //if there are no corresponding parts (e.g. no part for zeroes) return color as is
            if( parts.length <= partIndex ) return textColor;
            let formatPart = parts[partIndex];
            const colorMatch = formatPart.match(/(\[\w+\])/);
            if( colorMatch )
            {
                let color = colorMatch[0];
                switch( color )
                {
                    case '[Black]':
                        textColor = '#000000';
                        break;
                    case '[Blue]':
                        textColor = '#0000FF';
                        break;
                    case '[Green]':
                        textColor = '#00FF00';
                        break;
                    case '[Red]':
                        textColor = '#FF0000';
                        break;
                    case '[White]':
                        textColor = '#FFFFFF';
                        break;
                    case '[Yellow]':
                        textColor = '#FFFF00';
                        break;
                    case '[Cyan]':
                        textColor = '#00FFFF';
                        break;
                    case '[Magenta]':
                        textColor = '#FF00FF';
                        break;
                    default:
                        textColor = '#000000';
                }
            }
        }
        return textColor;
    }

    digitsAfterPoint():number
    {
        let testStringArr;
        if( this.formatString.indexOf(';') > -1 )
        { //has separate parts for positive and negative
            testStringArr = this.formatString.split(';')[0].match(/(0\.?0*)/);
        }
        else
        {
            testStringArr = this.formatString.match(/(0\.?0*)/);
        }
        if( !testStringArr ) return 0;
        let testString = testStringArr[0];
        if( null == testString || testString.indexOf('.') === -1 ) //string "0" - no digits after point at all
            return 0;
        return testString.split('.')[1].length;
    }

    private _getFormatCodePrefix():string
    {
        let testStringArr = this.formatString.match(/(#,#*)/);
        if( testStringArr )
        {
            return ',';
        }
        return '';
    }

    private _getFormatCodeSuffix():string
    {
        if( this.formatString.indexOf(';') > -1 )
        { //has separate parts for positive and negative
            let negativePart = this.formatString.split(';')[1];
            const colorMatch = negativePart.match(/(\[\w+\])/);
            if( colorMatch )
            {
                return '-';
            }
        }
        return '';
    }

    isStringOfThisFormat(formattedValue:string):boolean
    {
        return this.formatChecker(formattedValue);
    }

    extractValue(formattedValue:string):any
    {
        return this.valueExtractor(formattedValue);
    }

}

